import { Box, Flex, Heading, ListItem, OrderedList, Text, Link as ChakraLink } from '@chakra-ui/react';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { kebabCase } from 'lodash';

// @ts-ignore There is no types for this package
import AnchorLink from 'react-anchor-link-smooth-scroll-v2';
import { ApolloClient, gql, InMemoryCache } from '@apollo/client';
import { GET_FOOTER_DATA } from '@/graphql/footerQueries';
import { GET_HEADER_DATA } from '@/graphql/headerQueries';
import { handleError } from '@/helpers/handleError';
import { formatDate } from '@/helpers/formatDate';
import { BreadcrumbType } from '@/Types/types';
import { breadcrumbInitial } from '@/data/breadcrumbInitial';

import Footer from '@/components/Footer';
import HeaderMenu from '@/components/HeaderMenu';
import Breadcrumbs from '@/components/Breadcrumbs';
import { MarkdownRender } from '@/components/MarkdownRender';

interface Props {
  header: any;
  cookies: any;
  footer: any;
  navbar: any;
  homepage: any;
  breadcrumbs: BreadcrumbType[];
  privacyPolicy: any;
}

function PrivacyPolicyPage({ header, cookies, footer, navbar, homepage, breadcrumbs, privacyPolicy }: Props) {
  const seo = privacyPolicy.data?.attributes.seo[0] ?? null;
  const updatedAt = privacyPolicy.data?.attributes.updatedAt!;

  return (
    <Box bgColor="#FFFCF6">
      <HeaderMenu cookies={cookies.data} menu={header[0].items} submenu={navbar.data?.attributes.submenu_header} />
      <NextSeo title={seo.metaTitle} description={seo.metaDescription} />
      <Box
        width="100%"
        margin="0 auto"
        as="header"
      >
        <Flex
          direction="column"
          maxWidth="1216px"
          margin="0 auto"
          width={{ base: '90%', lg: '100%' }}
          pt={{base:"24px",lg:"80px"}}
        >
          <Flex
            direction={{ base: 'column', lg: 'column' }}
            height="100%"
            justifyContent="space-between"
            alignItems="start"
            mb={{ base: '32px', lg: '0' }}
          >
            <Box mb="32px">
              <Breadcrumbs data={breadcrumbs} />
            </Box>
            <Flex direction="column" w={{ base: '100%', lg: '629px' }} gap="16px">
              <Heading
                fontSize={{ base: '36px', lg: '36px' }}
                lineHeight={{ base: '40px', lg: '40px' }}
                fontWeight={{ base: '700', lg: '800' }}
                color="gray.800"
              >
                Política de Privacidade
              </Heading>
              <Text
                fontSize="16px"
                lineHeight="24px"
                color="gray.600"
              >
                Última atualização:{" "}
                {formatDate(updatedAt)}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Box>
      <Flex
        alignSelf="stretch"
        maxWidth="1216px"
        width={{base:"90%", lg:"100%"}}
        margin="32px auto 80px auto"
        gap="72px"
        alignItems="flex-start"
        flexDir={['column-reverse', 'row']}
      >
        <Flex flex="1 1 100%" flexDir="column" gap="4.5rem">
          {Boolean(privacyPolicy.data) &&
            privacyPolicy.data.attributes.paragraph.map((paragraph: any, index: number) => (
              <Flex key={paragraph.id} flexDir="column" gap="2rem" alignSelf="stretch">
                <Text fontSize="lg" fontWeight="bold" color="gray.900" id={kebabCase(paragraph.title)}>
                  {index + 1}. {paragraph.title}
                </Text>
                <MarkdownRender>{paragraph.description}</MarkdownRender>
              </Flex>
            ))}
        </Flex>
        <Flex flex={['1 0 0', '1 0 25.25rem']} flexDir="column" alignItems="center" gap="12px">
          <Text fontSize="lg" fontWeight="bold" color="gray.900" alignSelf="stretch">
            Conteúdo
          </Text>
          <Flex
            p="28px"
            flexDir="column"
            gap="24px"
            alignSelf="stretch"
            borderRadius="8px"
            bg="white"
            boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)"
          >
            <OrderedList
              display="flex"
              marginLeft="0"
              flexDir="column"
              rowGap="24px"
              style={{ counterReset: 'item' }}
              listStyleType="none"
            >
              {Boolean(privacyPolicy.data) &&
                privacyPolicy.data.attributes.paragraph.map((paragraph: any) => {
                  return (
                    <ListItem key={paragraph.id} style={{ counterIncrement: 'item' }}>
                      <ChakraLink
                        offset="120"
                        display="flex"
                        as={AnchorLink}
                        fontWeight="500"
                        fontSize="16px"
                        lineHeight="24px"
                        position="relative"
                        p="0"
                        pl="35px"
                        color="gray.600"
                        _hover={{
                          color: 'purple.800',
                          textDecor: 'none',
                        }}
                        _before={{
                          content: 'counter(item)',
                          display: 'inline-flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '24px',
                          height: '24px',
                          bgColor: 'gray.100',
                          borderRadius: '100%',
                          marginRight: '16px',
                          position: 'absolute',
                          left: '0',
                          top: '2px',
                          fontWeight: '600',
                          color: 'purple.500',
                        }}
                        href={`#${kebabCase(paragraph.title)}`}
                      >
                        {paragraph.title}
                      </ChakraLink>
                    </ListItem>
                  );
                })}
            </OrderedList>
          </Flex>
        </Flex>
      </Flex>
      <Footer menu={footer[0].items} />
    </Box>
  );
}

export default PrivacyPolicyPage;

export const getServerSideProps: GetServerSideProps = async ({ res, locale }) => {
  try {
    const client = new ApolloClient({
      uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
      cache: new InMemoryCache(),
    });

    const { data: headerData } = await client.query({
      query: GET_HEADER_DATA,
    });

    const { data: footerData } = await client.query({
      query: GET_FOOTER_DATA,
    });

    const breadcrumbs: BreadcrumbType[] = JSON.parse(JSON.stringify(breadcrumbInitial));

    breadcrumbs.push({
      href: '/politica-de-privacidade',
      label: 'Política de Privacidade',
    });

    const { data: legalData } = await client.query({
      query: gql`
        query GET_PRIVACY_POLICY {
          cookies: cookies {
            data {
              attributes {
                name
                description
                host
                duration
              }
            }
          }
          privacyPolicy {
            data {
              attributes {
                seo {
                  metaDescription
                  metaTitle
                  canonicalURL
                }
                updatedAt
                paragraph {
                  id
                  title
                  description
                }
              }
            }
          }
        }
      `,
    });

    return {
      props: {
        ...legalData,
        ...headerData,
        ...footerData,
        breadcrumbs,
      },
    }
  } catch (error) {
    handleError(error, 'PrivacyPolicyPage getServerSideProps');

    return {
      notFound: true,
    };
  }
};
